import {
  afterNextRender,
  ApplicationRef,
  Component,
  inject,
  isDevMode,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import {
  debounceTime,
  distinctUntilChanged,
  lastValueFrom,
  Subscription,
  tap,
} from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { RealtimeClientConfig } from '@gains-com/realtime';
import { DialogService } from '@gains-com/ui-angular';
import { AuthService } from './core/auth.service';
import { AuthComponent, AuthDialogData } from './auth/auth.component';
import { getDialogResult } from './utils/dialog';
import {
  SessionStorage,
  SessionStorageKey,
} from './core/session-storage.service';
import { VerisoulService } from './core/verisoul/verisoul.service.js';
import { LOGGER_FACTORY } from './core/logger/logger-factory.provider';
import { ServiceWorkerRegistrator } from './core/service-worker-registrator.service';
import { UserRealtimeChannel } from './core/realtime/user-realtime.service';
import { REALTIME } from './core/realtime/realtime.provider';
import { LocalStorage } from './core/local-storage/local-storage.service';
import { environment } from '../environments/environment';
import { User } from '@angular/fire/auth';
import { LocalStorageKey } from './core/local-storage/local-storage-keys';
import { DialogKey } from './shared/dialog-keys';
import { IntercomClient } from './core/intercom-client.service.js';
import { GlobalRealtimeChannel } from './core/realtime/global-realtime.service.js';
import { Analytics } from './core/analytics/analytics.service.js';
import { CrmPopupNotificationService } from './components/crm-popup/crm-popup-notification.service.js';
@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {

  private readonly _auth = inject(AuthService);
  private readonly _dialog = inject(DialogService);
  private readonly _verisoul = inject(VerisoulService);
  private readonly _serviceWorker = inject(ServiceWorkerRegistrator);
  private readonly _realtime = inject(REALTIME);
  private readonly _http = inject(HttpClient);
  private readonly _localStorage = inject(LocalStorage);
  private readonly _sessionStorage = inject(SessionStorage);
  private readonly _userRealtimeChannel = inject(UserRealtimeChannel);
  private readonly _crmPopupNotification = inject(CrmPopupNotificationService);
  private readonly _globalRealtimeChannel = inject(GlobalRealtimeChannel);
  private readonly _intercom = inject(IntercomClient);
  private readonly _logger = inject(LOGGER_FACTORY).create('AppComponent');
  private readonly _analytics = inject(Analytics);
  private readonly _router = inject(Router);
  private readonly _title = inject(Title);
  private readonly _appRef = inject(ApplicationRef);

  // TODO: We can abstract this behind a service that handles when users sign in or out.
  private _userStateSubscription = Subscription.EMPTY;
  private _verifiedUserStateSubscription = Subscription.EMPTY;
  private _routerEventsSubscription = Subscription.EMPTY;
  // Sign-in work that must also run for an unverified account: the verify-email
  // dialog *is* the pre-verification UX, and the fraud/analytics identities are
  // third-party calls, not our API.
  private _userState$ = this._auth.user$.pipe(
    distinctUntilChanged((u1, u2) => u1?.uid === u2?.uid),
    tap((user) => {
      if (user) {
        if (
          !this._router.url.includes('auth/action') &&
          !this._router.url.includes('mode=verifyEmail')
        ) {
          this._auth.checkUserPii(user).then();
        }
        this._verisoul.onAuth(user.uid, user.email);
        this._analytics.setUser(user.uid);
      } else {
        this._verisoul.resetSession();
        this._analytics.unsetUser();
      }
    })
  );
  // Everything that calls our API, which 403s all of it until the email is
  // verified — including the PubNub grant, so the channels can only be opened
  // from here. An unverified account is deliberately indistinguishable from a
  // signed-out one on this stream, which makes verification run through the same
  // path as a sign-in after an anonymous boot.
  private _verifiedUserState$ = this._auth.verifiedUser$.pipe(
    tap((user) => {
      if (this._realtime.ready) {
        // The client is already initialized which mean we've checked the user state before (meaning the user signed in or out).
        this._realtime.refreshAuthKey().then();
      } else {
        // This should theoretically only happen on the first time we see what the user state is.
        this._initializeRealtime(user).then(() =>
          this._globalRealtimeChannel.subscribe()
        );
      }

      this._intercom.refreshUser(!user).then();

      if (user) {
        this._userRealtimeChannel.subscribe(user.uid).then();
        // Session-start pull for the CRM popup channel — realtime signals
        // only cover popups dispatched while this session is live.
        this._crmPopupNotification.fetchAndShow().then();
      } else {
        this._userRealtimeChannel.unsubscribe();
      }
    })
  );

  constructor() {
    if (isDevMode()) {
      this._appRef.isStable
        .pipe(debounceTime(200), distinctUntilChanged())
        .subscribe((it: any) => {
          this._logger.log(`isStable ${it}`);
        });
    }

    this._serviceWorker.initialize().then();

    afterNextRender(() => {
      this._userStateSubscription = this._userState$.subscribe();
      this._verifiedUserStateSubscription =
        this._verifiedUserState$.subscribe();
    });
  }

  async ngOnInit() {
    const oauthFlow = this._sessionStorage.getItem(SessionStorageKey.OauthFlow);
    if (oauthFlow) {
      await getDialogResult(
        this._dialog.open<AuthComponent, AuthDialogData>(AuthComponent, {
          id: DialogKey.Auth,
          data: { initialView: 'login', oauthFlowProvider: oauthFlow.provider },
          disableClose: true,
        })
      );
    }
  }

  ngOnDestroy() {
    this._userStateSubscription.unsubscribe();
    this._verifiedUserStateSubscription.unsubscribe();
  }

  // TODO: Move behind its own service
  private async _initializeRealtime(user: User | null) {
    const realtimeAuthKeyCache = this._localStorage.getItem<{
      uid: string;
      token: string;
      exp: number;
    }>(LocalStorageKey.GainsRealtimeToken);
    const authKey = realtimeAuthKeyCache?.token;

    const refreshAuthKeyHandler = async () => {
      return await lastValueFrom(
        this._http
          .post<{ uid: string; token: string }>(
            `${environment.apiUrl}/realtime/token`,
            {}
          )
          .pipe(take(1))
      );
    };

    const onAuthKeyRefresh = (token: string) => {
      this._localStorage.setItem(LocalStorageKey.GainsRealtimeToken, { token });
    };

    const realtimeConfig: RealtimeClientConfig = {
      publishKey: environment.pubnub.publishKey,
      subscribeKey: environment.pubnub.subscribeKey,
      enableEventEngine: true,
      authKey,
      userId: user?.uid ?? 'anonymous',
      restore: true,
      refreshAuthKeyHandler,
      onAuthKeyRefresh,
      statusHandler: (x) => {
        if (x.category === 'PNDisconnectedUnexpectedlyCategory') {
          this._logger.info(
            `PNDisconnectedUnexpectedlyCategory status fired, attempting reconnection.`
          );
          this._realtime.reconnect();
        }
      },
      keepAlive: true,
      debug: environment.env !== 'production',
    };

    // Setting this to false as internet changes don't always get picked up otherwise (which causes retries to not happen).
    // According to the docs: If the browser fails to detect the network changes from WiFi to LAN or you get reconnection issues, set the flag to false.
    // This allows the SDK reconnection logic to take over.
    (realtimeConfig as any).listenToBrowserNetworkEvents = false;

    await this._realtime.initialize(realtimeConfig);
  }
}
